import { ItemTile } from '@/components/auctions/item-tile';
import { Layout } from '@/components/layouts/layout';
import { Button } from '@/components/ui/button';
import {
    useAddAuctionItem,
    useAuctionWithItems,
    useInventorySearchForAuction,
    useRemoveAuctionItem,
    useReorderAuctionItems,
    useUpdateAuction,
} from '@/hooks/use-auctions';
import type { AuctionItem } from '@/types/auctions';
import { getPlatformColor } from '@/types/auctions';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
    ArrowLeft,
    ChevronDown,
    ChevronUp,
    Clock,
    GripVertical,
    Plus,
    Search,
    Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/auctions/$auctionId/')({
    component: PlanningPage,
});

// ─── Sortable queue row ───────────────────────────────────────────────────────

interface SortableQueueItemProps {
    item: AuctionItem;
    index: number;
    total: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRemove: () => void;
}

function SortableQueueItem({
    item,
    index,
    total,
    onMoveUp,
    onMoveDown,
    onRemove,
}: SortableQueueItemProps) {
    const { ref, handleRef, isDragSource, isDropTarget, sortable } =
        useSortable({
            id: item.id,
            index,
        });

    return (
        <div
            ref={ref}
            className={`flex items-center gap-2.5 p-2 rounded-[11px] transition-opacity ${isDragSource ? 'bg-[#131b2f]' : isDropTarget ? 'bg-[#141e33]' : 'bg-[#0d1322]'} border ${isDropTarget ? 'border-[#3b4f7a]' : 'border-[#1a2235]'} ${isDragSource ? 'opacity-45' : 'opacity-100'}`}
        >
            <Button
                ref={handleRef}
                className={`cursor-grab active:cursor-grabbing shrink-0 touch-none text-[#5d667e] leading-0 p-0 has-[>svg]:px-0`}
                aria-label="Drag to reorder"
                variant="link"
            >
                <GripVertical size={16} />
            </Button>

            <span className="text-[11px] font-bold shrink-0 w-4.5 text-center font-mono text-[#5d667e]">
                {sortable.index + 1}
            </span>
            <div className="w-10 h-10 shrink-0 rounded overflow-hidden">
                <ItemTile item={item.inventory_items} borderRadius={8} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate text-[#e7eaf3]">
                    {item.inventory_items?.description ?? '—'}
                </div>
                <div className="text-xs text-[#6b7390]">
                    {item.inventory_items?.brand} · {item.inventory_items?.size}
                </div>
            </div>
            {/* Reorder arrows */}
            <div className="flex flex-col shrink-0">
                <button
                    onClick={onMoveUp}
                    disabled={index === 0}
                    className="cursor-pointer disabled:opacity-30 text-[#6b7390] leading-3"
                >
                    <ChevronUp size={13} />
                </button>
                <button
                    onClick={onMoveDown}
                    disabled={index === total - 1}
                    className="cursor-pointer disabled:opacity-30 text-[#6b7390] leading-3"
                >
                    <ChevronDown size={13} />
                </button>
            </div>
            <button
                onClick={onRemove}
                className="cursor-pointer shrink-0 p-1 rounded transition-colors text-[#5d667e] hover:text-[#f0556b]"
            >
                <Trash2 size={16} />
            </button>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function PlanningPage() {
    const { auctionId } = Route.useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useAuctionWithItems(auctionId);
    const updateAuction = useUpdateAuction(auctionId);
    const addItem = useAddAuctionItem(auctionId);
    const removeItem = useRemoveAuctionItem(auctionId);
    const reorder = useReorderAuctionItems(auctionId);

    const [invQuery, setInvQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Local order state — only set during/after a drag or arrow reorder
    const [localIds, setLocalIds] = useState<string[]>([]);

    const serverItems = useMemo(() => data?.items ?? [], [data]);

    // Use local order when it covers exactly the same IDs as the server;
    // fall back to server order when items are added/removed.
    const items = useMemo<AuctionItem[]>(() => {
        if (localIds.length === 0) return serverItems;
        const serverIdSet = new Set(serverItems.map((i) => i.id));
        const valid =
            localIds.length === serverItems.length &&
            localIds.every((id) => serverIdSet.has(id));
        if (!valid) return serverItems;
        return localIds.map((id) => serverItems.find((i) => i.id === id)!);
    }, [localIds, serverItems]);

    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(
            () => setDebouncedQuery(invQuery),
            250,
        );
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [invQuery]);

    const { data: searchResults = [] } = useInventorySearchForAuction(
        debouncedQuery,
        [],
    );

    const auction = data;

    function handleAdd(itemId: string) {
        const maxPos =
            items.length > 0 ? Math.max(...items.map((i) => i.position)) : -1;
        addItem.mutate(
            { itemId, position: maxPos + 1 },
            {
                onSuccess: () => toast.success('Added to queue'),
                onError: () => toast.error('Failed to add item'),
            },
        );
    }

    function handleRemove(id: string) {
        removeItem.mutate(id, {
            onError: () => toast.error('Failed to remove item'),
        });
    }

    function handleMove(id: string, direction: 'up' | 'down') {
        const currentIds = items.map((i) => i.id);
        const idx = currentIds.indexOf(id);
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= currentIds.length) return;
        [currentIds[idx], currentIds[swapIdx]] = [
            currentIds[swapIdx],
            currentIds[idx],
        ];
        setLocalIds(currentIds);
        reorder.mutate(
            currentIds.map((rid, pos) => ({ id: rid, position: pos })),
        );
    }

    function handleDragEnd(event: {
        source?: { id?: unknown };
        target?: { id?: unknown };
        canceled?: boolean;
    }) {
        if (event.canceled) return;
        const sourceId = String(event.source?.id ?? '');
        const targetId = String(event.target?.id ?? '');
        if (!sourceId || !targetId || sourceId === targetId) return;

        const currentIds = items.map((i) => i.id);
        const fromIdx = currentIds.indexOf(sourceId);
        const toIdx = currentIds.indexOf(targetId);
        if (fromIdx === -1 || toIdx === -1) return;

        currentIds.splice(fromIdx, 1);
        currentIds.splice(toIdx, 0, sourceId);
        setLocalIds(currentIds);
        reorder.mutate(currentIds.map((id, pos) => ({ id, position: pos })));
    }

    function handleStartAuction() {
        updateAuction.mutate(
            { status: 'active', started_at: new Date().toISOString() },
            {
                onSuccess: () =>
                    navigate({
                        to: '/auctions/$auctionId/live',
                        params: { auctionId },
                    }),
            },
        );
    }

    if (isLoading) {
        return (
            <Layout>
                <div className="max-w-6xl mx-auto px-4 py-8">
                    <div className="h-8 w-64 rounded-lg animate-pulse mb-4 bg-[#111726]" />
                    <div className="grid grid-cols-2 gap-4.5">
                        <div className="h-100 rounded-2xl animate-pulse bg-[#111726]" />
                        <div className="h-100 rounded-2xl animate-pulse bg-[#111726]" />
                    </div>
                </div>
            </Layout>
        );
    }

    if (!auction) return null;

    const platformColor = getPlatformColor(auction.platform);
    const queueItemIds = serverItems.map((i) => i.item_id);

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
                {/* Back */}
                <button
                    onClick={() => navigate({ to: '/auctions' })}
                    className="flex items-center gap-1 text-[13px] font-semibold mb-3.5 cursor-pointer text-[#9aa2c0]"
                >
                    <ArrowLeft size={16} /> Auctions
                </button>

                {/* Header */}
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-2.75 mb-1.75">
                            <h1 className="text-[30px] font-bold tracking-tight font-sans text-[#f5f7fb]">
                                {auction.title}
                            </h1>
                            <span className="flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-xl text-[#c3c9de] bg-[#161d2e] border border-[#242c40]">
                                <span
                                    className="w-1.75 h-1.75 rounded-full"
                                    style={{ background: platformColor }}
                                />
                                {auction.platform}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 text-[13.5px] text-[#9aa2c0]">
                            <span className="flex items-center gap-1.5">
                                <Clock size={14} />
                                {auction.scheduled_at
                                    ? new Date(
                                          auction.scheduled_at,
                                      ).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                      })
                                    : 'No date set'}
                            </span>
                            <span className="text-[#3a4258]">•</span>
                            <span>{items.length} items in queue</span>
                        </div>
                    </div>
                    <button
                        onClick={handleStartAuction}
                        disabled={items.length === 0 || updateAuction.isPending}
                        className="flex items-center gap-2.25 font-bold text-[14.5px] text-white px-5 py-3 rounded-[11px] cursor-pointer disabled:opacity-50 transition-opacity bg-rose-400"
                    >
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        Start Auction
                    </button>
                </div>

                {/* Two-panel grid */}
                <div className="grid grid-cols-2 gap-4.5 items-start">
                    {/* Left: Add from inventory */}
                    <div className="rounded-2xl p-4.5 bg-[#111726] border border-[#232b3f]">
                        <h2 className="font-semibold text-[17px] mb-1 [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Add from inventory
                        </h2>
                        <p className="text-[12.5px] mb-3.5 text-[#9aa2c0]">
                            Search and click to add items to your queue.
                        </p>

                        {/* Search */}
                        <div className="flex items-center gap-2.25 h-10 px-3.25 rounded-[11px] mb-3 bg-[#0c111d] border border-[#1d2536]">
                            <Search
                                size={16}
                                className="text-[#6b7390] shrink-0"
                            />
                            <input
                                value={invQuery}
                                onChange={(e) => setInvQuery(e.target.value)}
                                placeholder="Search inventory..."
                                className="flex-1 bg-transparent border-none outline-none text-[14px] text-[#f5f7fb]"
                            />
                        </div>

                        {/* Results */}
                        <div className="flex flex-col gap-1.5 max-h-130 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#222a3d_transparent]">
                            {searchResults.length === 0 && (
                                <p className="text-center py-8 text-[13px] text-[#5d667e]">
                                    {debouncedQuery
                                        ? 'No results found'
                                        : 'Type to search your inventory'}
                                </p>
                            )}
                            {searchResults.map((inv) => {
                                const inQueue = queueItemIds.includes(inv.id);
                                return (
                                    <div
                                        key={inv.id}
                                        className="flex items-center gap-2.75 p-2 rounded-[11px] bg-[#0d1322] border border-[#1a2235]"
                                    >
                                        <div className="w-10.5 h-10.5 shrink-0 rounded-xl overflow-hidden">
                                            <ItemTile
                                                item={inv}
                                                borderRadius={8}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13.5px] font-semibold truncate text-[#e7eaf3]">
                                                {inv.description ?? '—'}
                                            </div>
                                            <div className="text-xs text-[#6b7390]">
                                                {inv.brand} · {inv.size} ·{' '}
                                                {inv.condition}
                                            </div>
                                        </div>
                                        {inQueue ? (
                                            <span className="flex items-center gap-1 text-[12px] font-semibold px-2.75 py-1.75 shrink-0 text-[#34d27f]">
                                                ✓ In queue
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() =>
                                                    handleAdd(inv.id)
                                                }
                                                disabled={addItem.isPending}
                                                className="flex items-center gap-1 text-[12.5px] font-semibold px-3 py-1.75 rounded-[9px] cursor-pointer shrink-0 disabled:opacity-50 text-primary-400 bg-primary-600/10 border border-primary-600/50"
                                            >
                                                <Plus size={14} /> Add
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right: Queue */}
                    <div className="rounded-2xl p-4.5 bg-[#111726] border border-[#232b3f]">
                        <h2 className="font-semibold text-[17px] mb-1 [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Queue{' '}
                            <span className="text-[#6b7390] font-normal">
                                ({items.length})
                            </span>
                        </h2>
                        <p className="text-[12px] mb-3.5 text-[#6b7390]">
                            Drag to reorder or use the arrows. Order is followed
                            on Whatnot &amp; eBay Live.
                        </p>

                        <DragDropProvider onDragEnd={handleDragEnd}>
                            <div className="flex flex-col gap-1.5 max-h-135 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#222a3d_transparent]">
                                {items.length === 0 && (
                                    <p className="text-center py-12 text-[13px] text-[#5d667e]">
                                        No items yet — search and add from the
                                        left panel.
                                    </p>
                                )}
                                {items.map((item, idx) => (
                                    <SortableQueueItem
                                        key={item.id}
                                        item={item}
                                        index={idx}
                                        total={items.length}
                                        onMoveUp={() =>
                                            handleMove(item.id, 'up')
                                        }
                                        onMoveDown={() =>
                                            handleMove(item.id, 'down')
                                        }
                                        onRemove={() => handleRemove(item.id)}
                                    />
                                ))}
                            </div>
                        </DragDropProvider>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
